// src/services/snsService.js

import AWS from 'aws-sdk';

// Configure AWS SDK
// Configure AWS SDK
// Configure AWS SDK
// Configure AWS SDK
const configureAWS = () => {
    console.log("AWS Config Check:", {
      HAS_KEY_ID: Boolean(process.env.REACT_APP_AWS_ACCESS_KEY_ID),
      HAS_SECRET: Boolean(process.env.REACT_APP_AWS_SECRET_ACCESS_KEY),
      REGION: process.env.REACT_APP_AWS_REGION || 'us-east-2'
    });
    
    // Check if env variables are available
    if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
      console.error('AWS credentials missing. Using development mode fallback.');
      
      // For development, create a mock SNS object
      return {
        publish: (params) => {
          console.log('[DEV MODE] SMS would be sent to:', params.PhoneNumber);
          console.log('[DEV MODE] Message:', params.Message);
          
          return {
            promise: () => Promise.resolve({
              MessageId: 'mock-message-id-' + Date.now()
            })
          };
        }
      };
    }
  
    try {
      // Make sure credentials are strings
      const accessKeyId = String(process.env.REACT_APP_AWS_ACCESS_KEY_ID).trim();
      const secretAccessKey = String(process.env.REACT_APP_AWS_SECRET_ACCESS_KEY).trim();
      const region = (process.env.REACT_APP_AWS_REGION || 'us-east-2').trim();
      
      console.log(`Initializing AWS SDK with region: ${region}`);
      
      AWS.config.update({
        region: region,
        credentials: new AWS.Credentials({
          accessKeyId: accessKeyId,
          secretAccessKey: secretAccessKey
        })
      });
      
      return new AWS.SNS();
    } catch (error) {
      console.error('Error initializing AWS SDK:', error);
      
      // Return mock object as fallback
      return {
        publish: (params) => {
          console.log('[ERROR FALLBACK] SMS would be sent to:', params.PhoneNumber);
          console.log('[ERROR FALLBACK] Message:', params.Message);
          
          return {
            promise: () => Promise.resolve({
              MessageId: 'fallback-message-id-' + Date.now()
            })
          };
        }
      };
    }
};

// Generate a random 6-digit verification code
export const generateVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Format phone number to E.164 format (required by AWS SNS)
export const formatPhoneNumber = (phoneNumber) => {
    // Ensure phoneNumber is a string
    const phoneStr = String(phoneNumber || '');
    
    // Remove all non-digit characters
    const digits = phoneStr.replace(/\D/g, '');
    
    // For US numbers, ensure it has the country code
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // If it already has a country code
    if (digits.length > 10 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    
    // Return the original digits with a plus (handle international numbers)
    return `+${digits}`;
  };

// Storage for verification codes
// Note: In a production app, this should be on a secure server
const verificationCodes = new Map();

// Send verification code via SNS
// Send verification code via SNS
// Send verification code via SNS
export const sendVerificationCode = async (phoneNumber, code) => {
    try {
      // Check if we're forcing dev mode
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_REAL_SMS !== 'true') {
        console.log(`[DEV MODE] Verification code for ${phoneNumber}: ${code}`);
        return Promise.resolve({ success: true, code });
      }
      
      const SNS = configureAWS();
      
      if (!SNS) {
        throw new Error('Failed to configure AWS SNS');
      }
      
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
      console.log(`Attempting to send SMS to ${formattedPhoneNumber}`);
      
      // Prepare the message
      const message = `Your EzDrink verification code is: ${code}`;
      
      // Define parameters for the SNS publish
      const params = {
        Message: message,
        PhoneNumber: formattedPhoneNumber,
        MessageAttributes: {
          'AWS.SNS.SMS.SMSType': {
            DataType: 'String',
            StringValue: 'Transactional' // Transactional messages get higher priority
          }
        }
      };
      
      // Wrap this in a try/catch to get more detailed error information
      try {
        const result = await SNS.publish(params).promise();
        console.log('Verification code sent successfully:', result);
        return { success: true, messageId: result.MessageId };
      } catch (snsError) {
        console.error('Detailed SNS error:', snsError);
        throw snsError;
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      
      // Provide a fallback instead of throwing, which can break the UI
      console.log(`[ERROR FALLBACK] Verification code for ${phoneNumber}: ${code}`);
      return Promise.resolve({ 
        success: false, 
        code: code,
        error: error.message
      });
    }
  };
// Store verification code temporarily
export const storeVerificationCode = (phoneNumber, code) => {
  const formattedPhone = formatPhoneNumber(phoneNumber);
  const expiryTime = 10 * 60 * 1000; // 10 minutes in milliseconds
  
  // Store code with expiration time
  const codeData = {
    code,
    expiresAt: Date.now() + expiryTime
  };
  
  // Store in our in-memory Map
  verificationCodes.set(formattedPhone, codeData);
  
  // Set up auto-deletion after expiry
  setTimeout(() => {
    if (verificationCodes.has(formattedPhone)) {
      const storedData = verificationCodes.get(formattedPhone);
      if (storedData.code === code) {
        verificationCodes.delete(formattedPhone);
      }
    }
  }, expiryTime);
  
  // For development/testing, also store in sessionStorage
  try {
    sessionStorage.setItem(
      `verification_${formattedPhone}`, 
      JSON.stringify(codeData)
    );
  } catch (e) {
    console.warn('Could not store in sessionStorage', e);
  }
};

// Get stored verification code
export const getStoredVerificationCode = (phoneNumber) => {
  const formattedPhone = formatPhoneNumber(phoneNumber);
  
  // Try to get from in-memory Map first
  if (verificationCodes.has(formattedPhone)) {
    const codeData = verificationCodes.get(formattedPhone);
    
    // Check if code has expired
    if (Date.now() > codeData.expiresAt) {
      verificationCodes.delete(formattedPhone);
      return null;
    }
    
    return codeData.code;
  }
  
  // Fallback to sessionStorage (for development/testing)
  try {
    const storedData = sessionStorage.getItem(`verification_${formattedPhone}`);
    if (storedData) {
      const { code, expiresAt } = JSON.parse(storedData);
      
      // Check expiration
      if (Date.now() > expiresAt) {
        sessionStorage.removeItem(`verification_${formattedPhone}`);
        return null;
      }
      
      return code;
    }
  } catch (e) {
    console.warn('Could not retrieve from sessionStorage', e);
  }
  
  return null;
};

// Verify the code entered by the user
export const verifyPhoneCode = (phoneNumber, enteredCode) => {
  const storedCode = getStoredVerificationCode(phoneNumber);
  
  if (!storedCode) {
    return false; // No code stored or expired
  }
  
  const isValid = storedCode === enteredCode;
  
  if (isValid) {
    // Clean up on successful verification
    const formattedPhone = formatPhoneNumber(phoneNumber);
    verificationCodes.delete(formattedPhone);
    try {
      sessionStorage.removeItem(`verification_${formattedPhone}`);
    } catch (e) {
      console.warn('Could not remove from sessionStorage', e);
    }
  }
  
  return isValid;
};

// For testing purposes
export const _clearAllVerificationCodes = () => {
  verificationCodes.clear();
};

export default {
  generateVerificationCode,
  sendVerificationCode,
  storeVerificationCode,
  getStoredVerificationCode,
  verifyPhoneCode,
  formatPhoneNumber
};